<template>
    <div class="flex flex-row justify-end gap-1 p-1">
        <CardTile :class="phaseTileStyle">
            <template #header>
                <div v-if="phaseTileStatus === 'loaded'" class="flex flex-row items-center gap-1">
                    <PhasesIcon class="size-4" /> {{ $t('phases-tile-title') }}
                    <TextLink :to="linkToReferenceAllocation" class="font-light whitespace-nowrap">
                        {{ $t('phases-tile-title-reference-allocation') }}
                    </TextLink>
                    <span class="font-light whitespace-nowrap">
                        {{ $t('reference-allocation-duration-days', { durationInDays }) }}
                    </span>
                </div>
            </template>
            <template #content>
                <template v-if="phaseTileStatus === 'loaded'">
                    <PhasesTile>
                        <PhasesTilePhase v-for="phase in phaseTilePhases" v-bind="phase" :key="phase.labelShort" />
                    </PhasesTile>
                </template>
                <p
                    v-else-if="phaseTileStatus === 'no-data'"
                    class="flex flex-row items-center justify-center gap-2 p-2 text-gray-500 w-full h-full text-sm"
                >
                    <WarningIcon class="size-4" /> {{ $t('no-reference-allocation') }}
                </p>
                <p
                    v-else-if="phaseTileStatus === 'loading'"
                    class="flex flex-row items-center justify-center gap-2 p-2 text-gray-500 w-full h-full text-sm"
                >
                    <PendingIcon class="size-4 animate-spin" /> {{ $t('loading-tile-data') }}
                </p>
            </template>
        </CardTile>

        <CardTile :class="locationsTileStyle">
            <template v-if="locationChunks && locationChunks?.length > 0" #header>
                <div class="flex flex-row items-center gap-1">
                    <LocationOnIcon class="size-4" /> {{ $t('federal-states') }}
                </div>
            </template>
            <template #content>
                <p
                    v-if="loading"
                    class="flex flex-row items-center justify-center gap-2 p-2 text-gray-500 w-full h-full text-sm"
                >
                    <PendingIcon class="size-4 animate-spin" /> {{ $t('loading-tile-data') }}
                </p>
                <FederalStatesDotsTile
                    v-if="!loading && locationChunks && locationChunks.length > 0"
                    :sum="allocatedLocationsCount"
                >
                    <FederalStatesDotsRow v-for="(mappedLocations, index) in locationChunks" :key="index">
                        <FederalStatesDot
                            v-for="location in mappedLocations"
                            :key="location.id"
                            :state-name="location.name"
                            :status="location.status"
                            :default-tooltip-placement="location.defaultTooltipPlacement"
                            :color="getCategoryColor(event?.series.category)"
                        />
                    </FederalStatesDotsRow>
                </FederalStatesDotsTile>
            </template>
        </CardTile>
    </div>
</template>

<script setup lang="ts">
import CardTile from '@/components/CardTile/CardTile.vue';
import FederalStatesDot from '@/components/FederalStatesDotsTile/FederalStatesDot.vue';
import FederalStatesDotsRow from '@/components/FederalStatesDotsTile/FederalStatesDotsRow.vue';
import FederalStatesDotsTile from '@/components/FederalStatesDotsTile/FederalStatesDotsTile.vue';
import LocationOnIcon from '@/components/Icon/LocationOnIcon.vue';
import PendingIcon from '@/components/Icon/PendingIcon.vue';
import PhasesIcon from '@/components/Icon/PhasesIcon.vue';
import WarningIcon from '@/components/Icon/WarningIcon.vue';
import { usePhaseTileData } from '@/components/PhasesTile/composables/usePhaseTileData';
import PhasesTile from '@/components/PhasesTile/PhasesTile.vue';
import PhasesTilePhase from '@/components/PhasesTile/PhasesTilePhase.vue';
import TextLink from '@/components/TextLink/TextLink.vue';
import { AllocationUsage } from '@/components/Timeline/allocation/types';
import { DocumentNode } from 'graphql';
import { computed } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { useExternalEventInjection } from '../composables/useExternalEventInjection';
import { useExternalEventLocationDotsData } from '../composables/useExternalEventLocationDotsData';
import { getCategoryColor } from '../externalEvent';

const props = defineProps<{
    variantId: string;
    variantIndex: number;
    phaseTypes: AllocationUsage[];
    referenceAllocationQueryDocument: DocumentNode;
    referenceAllocationId?: string;
    referenceAllocationLinkLabel: string;
    isNational: boolean;
    linkToReferenceAllocation: RouteLocationRaw;
}>();

const { event } = useExternalEventInjection();

const phaseTileStyle = 'min-w-[289px]';
const locationsTileStyle = 'min-w-[242px]';

const eventId = computed(() => event.value?.id);
const variantId = computed(() => props.variantId);
const referenceAllocationId = computed(() => props.referenceAllocationId);
const isNational = computed(() => props.isNational);

const { loading, locationChunks, allocatedLocationsCount } = useExternalEventLocationDotsData(
    eventId,
    variantId,
    isNational,
);

const { durationInDays, phaseTilePhases, phaseTileStatus } = usePhaseTileData(
    props.referenceAllocationQueryDocument,
    props.phaseTypes,
    referenceAllocationId,
);
</script>
